import { Link } from "react-router-dom";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { formatMoney } from "@/pages/InvoiceManagement/utils/money";
import { V2_COLORS } from "@/pages/InvoiceManagement/v2/constants/colors";
import type { VendorRfqSummary } from "../types";
import { rfqDetailPath } from "../constants/paths";
import { budgetLabel } from "../utils/budget";
import { formatCalendarDate } from "../utils/deadline";
import { submissionOf } from "../utils/response";
import { describeRfq, projectLine } from "../utils/rfqCardCopy";
import RfqStatusBadge from "./RfqStatusBadge";

interface RfqCardProps {
  rfq: VendorRfqSummary;
  now: number;
}

const linkStyle = { color: V2_COLORS.primary, fontWeight: 600, textDecoration: "none" };

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography fontSize="11px" sx={{ color: V2_COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {label}
      </Typography>
      <Typography fontSize="14px" fontWeight={600}>
        {value}
      </Typography>
    </Box>
  );
}

export default function RfqCard({ rfq, now }: RfqCardProps) {
  const copy = describeRfq(rfq, now);
  const submission = submissionOf(rfq);
  const budget = budgetLabel(rfq.project_budget);

  return (
    <Box data-testid="rfq-card" sx={{ backgroundColor: "white", border: `1px solid ${V2_COLORS.border}`, borderRadius: "8px", p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
        <Link to={rfqDetailPath(rfq.id)} style={{ ...linkStyle, fontSize: "16px" }}>
          {rfq.rfq_number}
        </Link>
        <RfqStatusBadge status={rfq.status} />
        {copy.mode && <Chip size="small" label={copy.mode} variant="outlined" />}
        <Box sx={{ flex: 1 }} />
        {copy.countdown && (
          <Chip
            size="small"
            label={copy.countdown}
            sx={{
              fontWeight: 600,
              color: copy.windowOpen ? V2_COLORS.warning : V2_COLORS.textSecondary,
              backgroundColor: copy.windowOpen ? V2_COLORS.warningBg : V2_COLORS.bgMuted,
            }}
          />
        )}
      </Stack>
      <Typography fontSize="13px" sx={{ color: V2_COLORS.textSecondary, mt: 0.5 }}>
        {projectLine(rfq)}
      </Typography>
      <Stack direction="row" spacing={4} sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 1 }}>
        <Figure label="Ask total" value={formatMoney(rfq.ask_total)} />
        {submission && <Figure label="Your total" value={formatMoney(submission.bid_total)} />}
        {budget && <Figure label="Project budget" value={budget} />}
        <Figure label="Expected delivery" value={formatCalendarDate(rfq.expected_delivery_date)} />
        <Figure label="Site pincode" value={rfq.site_pincode} />
        <Figure label="Lines" value={String(rfq.line_count)} />
      </Stack>
      <Stack data-testid="rfq-trades" direction="row" flexWrap="wrap" sx={{ mt: 1.5, gap: 1 }}>
        {rfq.trades.map((trade) => (
          <Chip key={trade} size="small" label={trade} variant="outlined" />
        ))}
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mt: 1.5 }}>
        <Typography fontSize="13px" fontWeight={500}>
          {copy.headline}
        </Typography>
        <Link to={copy.action.to} style={linkStyle}>
          {copy.action.label}
        </Link>
      </Stack>
    </Box>
  );
}
